import { createFileRoute, Navigate } from "@tanstack/react-router";
import { z } from "zod";
import type { TestResultImportId, TestSessionId } from "@brewbase/db/types";
import { trpc } from "@/hooks/trpc";
import { ImportDialog } from "@/features/test-orders/import-dialog";

/**
 * A review is opened from its order page (`?import=<id>` there). This route
 * keeps old links working and hosts an import that has no order yet — results
 * read in without one, historical files included. Committing opens the order
 * and lands there.
 */
export const Route = createFileRoute("/analytics/external/imports/$import_id")({
  component: ImportReviewRoute,
  params: z.object({
    import_id: z.coerce.number().transform((v) => v as TestResultImportId),
  }),
});

function ImportReviewRoute() {
  const { import_id } = Route.useParams();
  const navigate = Route.useNavigate();
  const { data: imp, isLoading } =
    trpc.tests.resultImports.get.useQuery(import_id);

  if (isLoading) {
    return <div className="px-4 py-6 text-sm text-olive-9">Loading…</div>;
  }
  if (imp?.test_session_id) {
    return (
      <Navigate
        to="/analytics/external/orders/$session_id"
        params={{ session_id: imp.test_session_id as TestSessionId }}
        search={{ import: import_id }}
        replace
      />
    );
  }
  return (
    <ImportDialog
      importId={import_id}
      onClose={() =>
        navigate({
          to: "/analytics/external",
          search: { tab: "orders" },
        })
      }
      onCommitted={({ sessionId }) =>
        navigate({
          to: "/analytics/external/orders/$session_id",
          params: { session_id: sessionId as TestSessionId },
          replace: true,
        })
      }
    />
  );
}
